import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, Check, Info, X } from 'lucide-react';
import { getQuizAttemptDetail, submitQuizFeedback } from '../../api/teacher';
import toast from 'react-hot-toast';
import { useI18n } from '../../i18n/I18nContext';
import { buildAttemptReview } from '../../utils/quizAttempt';
import { formatDateTime, formatPercent } from '../../utils/datetime';
import { failure, failureText } from './apiError';
import { useCourseTitle } from '../../hooks/useCourseTitle';
import PageHead from '../../components/PageHead/PageHead';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import './QuizFeedback.css';

function QuizFeedback() {
  const { attemptId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [searchParams] = useSearchParams();
  // Prefer the query string so this page survives a refresh/bookmark/shared link;
  // location.state (set by StudentDetail's "Provide feedback" click) is only a
  // fallback for the click-through case, since it does not survive a hard reload.
  const courseId = searchParams.get('courseId') || location.state?.courseId;
  const studentName = searchParams.get('studentName') || location.state?.studentName;

  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // { title, detail }: what failed, and (when the API client has worded the cause) why
  const [error, setError] = useState(null);
  // The attempt is gone (or never was): there is nothing to retry
  const [missing, setMissing] = useState(false);
  const [formData, setFormData] = useState({
    feedback: '',
    suggestions: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const redirectTimer = useRef(null);
  // An attempt knows its own student, so a link without ?studentId= still works.
  const studentId = searchParams.get('studentId') || location.state?.studentId || attempt?.userId;
  // Feedback already given on this attempt: the form edits it, and submitting replaces it.
  const existingFeedback = attempt?.teacherFeedback || null;
  // The course the quiz belongs to, over the title (the generic parent label until it is known)
  const courseTitle = useCourseTitle(courseId);
  const review = attempt ? buildAttemptReview(attempt) : null;

  // The redirect after a saved feedback must not fire once the teacher has already moved on.
  useEffect(() => () => clearTimeout(redirectTimer.current), []);

  const fetchAttemptDetail = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getQuizAttemptDetail(attemptId);
      setAttempt(data);
      if (data?.teacherFeedback?.content) {
        // Prefill, unless the teacher has already started typing.
        setFormData(prev => (prev.feedback ? prev : { ...prev, feedback: data.teacherFeedback.content }));
      }
      setError(null);
      setMissing(false);
    } catch (err) {
      // Never surface the raw backend string here: a validation error such as "Validation failed
      // (uuid is expected)" for a malformed attemptId in the URL is untranslated and not meant for
      // end users. The page says it in its own words (by status); the API client's own sentence
      // (offline, timed out, server down) rides along as the detail.
      // apiClient's response interceptor (src/api/client.js) rewrites rejected errors into a plain
      // { message, code, ...data } object, so the status lives on `.code`.
      const status = err?.code ?? err?.response?.status;
      setMissing(status === 404);
      setError(status === 404
        ? { title: t('teacher.quizFeedback.notFound') }
        : failure(err, t('teacher.quizFeedback.failedToLoad')));
    } finally {
      setLoading(false);
    }
  }, [attemptId, t]);

  useEffect(() => {
    if (!attemptId || !courseId) {
      // courseId/studentId/studentName travel via the ?courseId= query string (preferred,
      // survives a refresh/bookmark) or location.state (in-app click fallback). Neither
      // was present, so there is genuinely no course context to load this attempt against.
      toast.error(t('teacher.quizFeedback.missingContext'));
      navigate('/teacher/dashboard');
      return;
    }
    fetchAttemptDetail();
  }, [attemptId, courseId, navigate, fetchAttemptDetail, t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.feedback.trim()) {
      setError({ title: t('teacher.quizFeedback.feedbackRequired') });
      return;
    }

    if (!courseId || !studentId) {
      setError({ title: t('teacher.quizFeedback.missingContext') });
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await submitQuizFeedback(courseId, studentId, attemptId, {
        content: formData.feedback.trim(),
        suggestions: formData.suggestions.trim() || undefined,
      });
      setSubmitted(true);
      redirectTimer.current = setTimeout(() => {
        navigate(returnTo);
      }, 2000);
    } catch (err) {
      // Same rule as fetchAttemptDetail: the page's own words by status, never the backend's text.
      const status = err?.code ?? err?.response?.status;
      setError(status === 404
        ? { title: t('teacher.quizFeedback.notFound') }
        : failure(err, t('teacher.quizFeedback.failedToSubmit')));
    } finally {
      setSubmitting(false);
    }
  };

  // Feedback is given from a student's page: that is where "back", "cancel" and a saved feedback lead.
  // A link without the student (or the course) falls back to the dashboard.
  const returnTo = courseId && studentId
    ? { pathname: `/teacher/students/${studentId}`, search: `?courseId=${encodeURIComponent(courseId)}` }
    : '/teacher/dashboard';
  const goBack = () => navigate(returnTo);
  const back = {
    onClick: goBack,
    label: courseId && studentId ? t('teacher.quizFeedback.backToStudent') : t('teacher.quizFeedback.backToDashboard'),
  };
  const eyebrow = courseTitle || t('teacher.studentDetail.parentName');

  if (loading) {
    return (
      <div className="quiz-feedback">
        <PageHead back={back} eyebrow={eyebrow} title={t('teacher.quizFeedback.title')} />
        <LoadingState message={t('teacher.quizFeedback.loading')} skeleton="form" rows={4} />
      </div>
    );
  }

  if (error && !attempt) {
    return (
      <div className="quiz-feedback">
        <PageHead back={back} eyebrow={eyebrow} title={t('teacher.quizFeedback.title')} />
        {missing ? (
          <PageEmptyState
            variant="search"
            title={error.title}
            actionLabel={back.label}
            onAction={goBack}
          />
        ) : (
          <PageEmptyState variant="error" title={error.title} description={error.detail} onRetry={fetchAttemptDetail} />
        )}
      </div>
    );
  }

  // The label string carries its own "*"; the required mark is drawn in the danger tone instead.
  const feedbackLabel = t('teacher.quizFeedback.feedbackLabel').replace(/\s*\*\s*$/, '');

  return (
    <div className="quiz-feedback">
      <PageHead
        back={back}
        eyebrow={eyebrow}
        title={t('teacher.quizFeedback.title')}
        description={t('teacher.quizFeedback.subtitle', { name: studentName })}
      />

      {submitted && (
        <div className="ui-alert qf-alert" data-tone="success" role="status">
          <Check aria-hidden="true" />
          <p>{t('teacher.quizFeedback.submittedSuccess')}</p>
        </div>
      )}

      {error && (
        <div className="ui-alert qf-alert" role="alert">
          <AlertCircle aria-hidden="true" />
          <p>{failureText(error)}</p>
        </div>
      )}

      {attempt && (
        <div className="feedback-container qf-layout">
          {/* Quiz Details */}
          <section className="section-card quiz-details-card">
            <div className="section-head">
              <h2 className="section-head__title">{t('teacher.quizFeedback.quizDetails')}</h2>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="label">{t('teacher.quizFeedback.quizTitleLabel')}</span>
                <span className="value">{review.title || t('teacher.quizFeedback.quizTitleFallback')}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t('teacher.quizFeedback.studentScoreLabel')}</span>
                <span className="value score">{review.score}/{review.total} ({formatPercent(review.percent)})</span>
              </div>
              <div className="detail-item">
                <span className="label">{t('teacher.quizFeedback.passRequiredLabel')}</span>
                <span className="value">{formatPercent(review.passingScore)}</span>
              </div>
              <div className="detail-item">
                <span className="label">{t('teacher.quizFeedback.submittedLabel')}</span>
                <span className="value">
                  {review.submittedAt ? formatDateTime(review.submittedAt, { lang, year: 'always' }) : '-'}
                </span>
              </div>
            </div>

            {/* Questions Breakdown */}
            {review.questions.length > 0 && (
              <div className="questions-section">
                <h3 className="qf-subtitle">{t('teacher.quizFeedback.studentAnswers')}</h3>
                <div className="questions-list">
                  {review.questions.map((question, idx) => (
                    <div key={question.id || idx} className="question-item">
                      <div className="question-header">
                        <span className="question-number">{t('teacher.quizFeedback.questionNumber', { number: idx + 1 })}</span>
                        <span className={`question-status ds-badge ${question.isCorrect ? 'ds-badge-success' : 'ds-badge-danger'}`}>
                          {question.isCorrect
                            ? <><Check size={13} aria-hidden="true" /> {t('teacher.quizFeedback.correct')}</>
                            : <><X size={13} aria-hidden="true" /> {t('teacher.quizFeedback.incorrect')}</>}
                        </span>
                      </div>
                      <div className="question-text">{question.text}</div>
                      <div className="student-answer">
                        <strong>{t('teacher.quizFeedback.studentAnswer')}</strong> {question.studentAnswer || t('teacher.quizFeedback.noAnswer')}
                      </div>
                      {!question.isCorrect && question.correctAnswer && (
                        <div className="correct-answer">
                          <strong>{t('teacher.quizFeedback.correctAnswer')}</strong> {question.correctAnswer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Feedback Form */}
          <section className="section-card feedback-form-card">
            <div className="section-head">
              <h2 className="section-head__title">{t('teacher.quizFeedback.yourFeedback')}</h2>
            </div>
            {existingFeedback && (
              <div className="ui-alert edit-notice" data-tone="info">
                <Info aria-hidden="true" />
                <p>{t('teacher.quizFeedback.editingNotice')}</p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="qf-form">
              {/* Feedback Field */}
              <div className="ui-field form-group">
                <label className="ui-label" htmlFor="feedback">
                  {feedbackLabel} <span className="ui-req" aria-hidden="true">*</span>
                </label>
                <textarea
                  id="feedback"
                  name="feedback"
                  value={formData.feedback}
                  onChange={handleInputChange}
                  placeholder={t('teacher.quizFeedback.feedbackPlaceholder')}
                  rows="8"
                  className="feedback-textarea"
                  required
                />
                <p className="ui-hint">{t('teacher.quizFeedback.feedbackHint')}</p>
              </div>

              {/* Suggestions Field */}
              <div className="ui-field form-group">
                <label className="ui-label" htmlFor="suggestions">{t('teacher.quizFeedback.suggestionsLabel')}</label>
                <textarea
                  id="suggestions"
                  name="suggestions"
                  value={formData.suggestions}
                  onChange={handleInputChange}
                  placeholder={t('teacher.quizFeedback.suggestionsPlaceholder')}
                  rows="6"
                  className="suggestions-textarea"
                />
              </div>

              {/* Action Buttons */}
              <div className="form-actions">
                <button
                  type="button"
                  className="ds-btn ds-btn-secondary"
                  onClick={goBack}
                  disabled={submitting}
                >
                  {t('teacher.quizFeedback.cancel')}
                </button>
                <button
                  type="submit"
                  className={`ds-btn ds-btn-primary${submitting ? ' is-loading' : ''}`}
                  disabled={submitting || submitted}
                  aria-busy={submitting || undefined}
                >
                  {existingFeedback ? t('teacher.quizFeedback.updateFeedback') : t('teacher.quizFeedback.submitFeedback')}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}

export default QuizFeedback;
